import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { apiClient } from '@/api/client'
import { useAuth } from '@/context/AuthContext'
import type { AuthResponse } from '@/api/auth'

const schema = z.object({
  current_password: z.string().min(1, '現在のパスワードを入力してください'),
  new_password: z.string().min(8, '8文字以上にしてください'),
  new_password2: z.string(),
}).refine((d) => d.new_password === d.new_password2, {
  message: 'パスワードが一致しません',
  path: ['new_password2'],
})

type FormValues = z.infer<typeof schema>

export default function ChangePasswordPage() {
  const navigate = useNavigate()
  const { updateTokens } = useAuth()
  const [isLoading, setIsLoading] = useState(false)

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
  })

  const onSubmit = async (values: FormValues) => {
    setIsLoading(true)
    try {
      const { data } = await apiClient.post<AuthResponse>('/auth/change-password/', values)
      updateTokens(data.access, data.refresh)
      toast.success('パスワードを変更しました')
      reset()
      navigate('/settings', { replace: true })
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(msg ?? 'パスワードの変更に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-[env(safe-area-inset-top)] z-10 flex h-14 items-center bg-background border-b px-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mr-2 flex items-center justify-center min-h-[44px] min-w-[44px] -ml-2"
        >
          <ChevronLeft size={20} />
        </button>
        <h1 className="text-base font-semibold">パスワードを変更</h1>
      </header>

      <div className="max-w-md mx-auto px-4 py-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">現在のパスワード</label>
            <Input {...register('current_password')} type="password" autoComplete="current-password" />
            {errors.current_password && <p className="text-xs text-destructive">{errors.current_password.message}</p>}
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">新しいパスワード（8文字以上）</label>
            <Input {...register('new_password')} type="password" autoComplete="new-password" />
            {errors.new_password && <p className="text-xs text-destructive">{errors.new_password.message}</p>}
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">新しいパスワード（確認）</label>
            <Input {...register('new_password2')} type="password" autoComplete="new-password" />
            {errors.new_password2 && <p className="text-xs text-destructive">{errors.new_password2.message}</p>}
          </div>

          <Button type="submit" className="w-full mt-2" disabled={isLoading}>
            {isLoading ? '変更中...' : 'パスワードを変更'}
          </Button>
        </form>
      </div>
    </div>
  )
}
